import { PureComponent, ReactNode } from 'react';
import { AntTooltip } from '../../../../common/AntTooltip';
import { ChartType } from '../DentalChart';
import { connect } from 'react-redux';
import {
    getChartType,
    getDentalExaminations,
    getPatientPersonalProfile,
    getPrescription,
    getRxDoctor,
} from '../../../../../store/selectors/prescriptionPad';
import { GlobalState } from '../../../../../store/reducers';
import { ORBIT } from '../../../../../api/constants';
import { track } from '../../../../../tracking';
import {
    DentalExamination,
    Doctor,
    PatientPersonalProfile,
} from '../../../../../store/types/prescription';
import { getAgeString } from '../../../../../store/utils';
import {
    CUSTOM_CLASS_IDS,
    getDentalChartContent,
    getHeaderAndFooterContent,
    getVisualExaminations,
} from '../Utils';
import capitalize from 'lodash/capitalize';
import { getAddPDForms } from '../../../../../pages/utils';

interface Props {
    chartType: ChartType;
    onChartTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    toggleIsDentalVisualisationsModalVisible: () => void;
    patientPersonalProfile?: PatientPersonalProfile;
    dentalExaminations?: DentalExamination[];
    prescriptionId: string;
    doctor?: Doctor;
}

interface State {
    isPrintChartLoading: boolean;
}

class RenderHeaderRight extends PureComponent<Props> {
    state: State = {
        isPrintChartLoading: false,
    };

    onPrintDentalChart = async () => {
        const { patientPersonalProfile, dentalExaminations, prescriptionId, doctor } = this.props;
        const { printIframe } = this;
        const printWindow = document.createElement('iframe');
        const dentalChartContent = getDentalChartContent();
        if (!printWindow || !dentalChartContent) {
            return;
        }
        const visualExaminations = getVisualExaminations(dentalExaminations);
        const visualExaminationsContext = visualExaminations
            ?.map(
                (i) =>
                    `<div class="flex items-center space-x-8">
                        <p class="text-11">${capitalize(i.replaceAll('-', ' '))}:</p>
                        <p><img src=${`/images/dental-images/${i}.webp`} class="${
                        CUSTOM_CLASS_IDS.has(i) ? 'max-w-20 max-h-24' : `max-w-20 max-h-16`
                    }"/></p>
                    </div>`,
            )
            .join('');
        const patientDetails = [
            patientPersonalProfile?.gender,
            getAgeString(
                {
                    age: patientPersonalProfile?.age?.value,
                    month: patientPersonalProfile?.age?.month,
                    dob: patientPersonalProfile?.age?.dob,
                },
                ' ',
            ),
            `${patientPersonalProfile?.phone?.c || ''}${patientPersonalProfile?.phone?.n || ''}`,
        ].filter(Boolean);

        const pdData = patientDetails.join(', ');
        this.setState({
            isPrintChartLoading: true,
        });

        if (!doctor) {
            return;
        }
        const ptFormDets = await getAddPDForms();
        const { headerHtml, footerHtml } = getHeaderAndFooterContent(
            doctor,
            prescriptionId,
            ptFormDets,
        );
        printWindow.srcdoc = `
            <!DOCTYPE html>
            <html lang="en">
                <head>
                    <title>Print</title>
                    <link href=${ORBIT} rel="stylesheet">
                    <style>
                       .selected-tooth {
                            border: 1px solid #215FFF;
                        }
                    </style>
                </head>
                <body>
                    <div class="h-full w-full flex flex-col">
                        ${headerHtml}
                        <div class="flex justify-between pt-8">
                            <div class="text-13">
                                <span class="text-13 bold">${
                                    patientPersonalProfile?.name || ''
                                }, </span>
                                                ${pdData}
                            </div>
                        </div>
                        <div class="flex-1 space-y-12">
                            <div>${dentalChartContent}</div>
                            <div class="grid grid-cols-4 gap-x-12 gap-y-12">
                                ${visualExaminationsContext}
                            </div>
                        </div>
                        ${footerHtml}
                    </div>
                </body>
            </html>
        `;
        document.body.appendChild(printWindow);
        printWindow.onload = () => {
            const pW = printWindow.contentWindow;
            if (!pW) return;
            const images = pW.document.images;
            let loadedImagesCount = 0;
            const checkAllImagesLoaded = () => {
                loadedImagesCount++;
                if (loadedImagesCount === images.length) printIframe(pW, printWindow);
            };
            if (images.length === 0) {
                printIframe(pW, printWindow);
            } else {
                for (const img of images) {
                    if (img.complete) checkAllImagesLoaded();
                    else {
                        img.onload = checkAllImagesLoaded;
                        img.onerror = checkAllImagesLoaded;
                    }
                }
            }
        };
        track('DENTAL_CHART_PRINT');
    };

    printIframe = (pW: Window, printWindow: HTMLIFrameElement) => {
        pW.focus();
        pW.print();
        pW.onafterprint = () => {
            document.body.removeChild(printWindow);
        };
        this.setState({
            isPrintChartLoading: false,
        });
    };

    render(): ReactNode {
        const {
            chartType,
            onChartTypeChange,
            toggleIsDentalVisualisationsModalVisible,
        } = this.props;
        const { isPrintChartLoading } = this.state;
        const { onPrintDentalChart } = this;

        return (
            <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-4 text-neutral-black text-12 leading-3 bg-neutral-9 rounded-4 px-8 h-26">
                    <div className="flex items-baseline space-x-4">
                        <p>Type:</p>
                        <select
                            value={chartType}
                            onChange={onChartTypeChange}
                            className="bg-transparent text-12 cursor-pointer"
                        >
                            <option value="adult">Adult Dentition</option>
                            <option value="pedia">Pedia Dentition</option>
                            <option value="mixed">Mixed Dentition</option>
                        </select>
                    </div>
                </div>
                <button
                    className="flex items-center space-x-8 border border-darwin-accent-bright-blue-800 rounded-4 px-6 h-26 bg-darwin-accent-bright-blue-800 text-white rounded-4"
                    onClick={onPrintDentalChart}
                >
                    {isPrintChartLoading ? (
                        <svg
                            className="w-13 h-13 fill-white animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                        </svg>
                    ) : (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                            className="w-13 h-13 fill-white"
                        >
                            <path d="M128 0C92.7 0 64 28.7 64 64v96h64V64H354.7L384 93.3V160h64V93.3c0-17-6.7-33.3-18.7-45.3L400 18.7C388 6.7 371.7 0 354.7 0H128zM384 352v32 64H128V384 368 352H384zm64 32h32c17.7 0 32-14.3 32-32V256c0-35.3-28.7-64-64-64H64c-35.3 0-64 28.7-64 64v96c0 17.7 14.3 32 32 32H64v64c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V384zM432 248a24 24 0 1 1 0 48 24 24 0 1 1 0-48z" />
                        </svg>
                    )}
                    <p className="text-12 font-700 whitespace-nowrap">Print dental chart</p>
                </button>
                <AntTooltip content="Show Visualisations" placement="left">
                    <div>
                        <div
                            className="flex items-center justify-center bg-neutral-9 rounded-full w-26 h-26 cursor-pointer"
                            onClick={toggleIsDentalVisualisationsModalVisible}
                        >
                            <svg
                                className="w-14 h-14 fill-neutral-3"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM296 336h-16V248C280 234.8 269.3 224 256 224H224C210.8 224 200 234.8 200 248S210.8 272 224 272h8v64h-16C202.8 336 192 346.8 192 360S202.8 384 216 384h80c13.25 0 24-10.75 24-24S309.3 336 296 336zM256 192c17.67 0 32-14.33 32-32c0-17.67-14.33-32-32-32S224 142.3 224 160C224 177.7 238.3 192 256 192z"></path>
                            </svg>
                        </div>
                    </div>
                </AntTooltip>
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    chartType: getChartType(state),
    patientPersonalProfile: getPatientPersonalProfile(state),
    dentalExaminations: getDentalExaminations(state),
    prescriptionId: getPrescription(state)?.id || '',
    doctor: getRxDoctor(state),
}))(RenderHeaderRight);
